import { c as _c } from "react/compiler-runtime";
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Box, Button, chakra, Flex, Icon, IconButton, Separator, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { IoEllipsisVertical } from 'react-icons/io5';
import { LuArrowDown, LuArrowUp, LuChevronsUpDown, LuLock } from 'react-icons/lu';
import InferenceAlgorithmModal, { InferenceAlgorithmModalProps } from '@/components/AppBar/SelectPopover/InferenceAlgorithmModal';
import { formatEditor } from '@/components/pages/Home/NetworkOwnerFilter';
import { Checkbox } from '@/components/ui/checkbox';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { PopoverArrow, PopoverBody, PopoverCloseTrigger, PopoverContent, PopoverHeader, PopoverRoot, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useSafeVirtualizer } from '@/hooks/useSafeVirtualizer';
import { useHandleOutsideClick } from '@/hooks/utils/useHandleOutsideClick';
import { InferenceAlgorithm } from '@/services/bayesianApi/models';
import { Z_INDEX } from '@/services/constraints/style';
import { NetworkStatus } from '@/services/models/enums';
import { CopyKind } from './copyKind';
import InputModal, { InputModalProps } from './InputModal';
export type Option = {
  id: string;
  name: string;
  inferenceAlgorithm?: InferenceAlgorithm;
  editedOn?: string;
  ownerName?: string | null;
  url: string;
  status?: NetworkStatus;
  /** Email of the user who made the most recent history entry on this
   * network. Surfaced in the Home page's inline browser only. */
  lastEditor?: string | null;
  /** Verbose calendar form of `editedOn` (e.g. "Today at 4:18 PM");
   * exposed alongside the compact relative form for tooltips. */
  editedOnExact?: string;
  /** Compact relative creation time (e.g. "3 days ago"); Home browser only. */
  createdOn?: string;
  /** Verbose calendar form of `createdOn`, exposed for tooltips. */
  createdOnExact?: string;
  /** True when the network is a Marvin-writable copy (set by the
   * `copy_network_for_marvin` tool). Surfaced as a badge so users
   * can spot which networks the assistant is allowed to edit. */
  marvinWritable?: boolean;
  /** True for the canonical master network (3.X development). KBE-586. */
  isMain?: boolean;
  /** True for networks auto-created by the scheduled-copy job. KBE-586. */
  isRegularCopy?: boolean;
  /** True for release branches (copies whose purpose is cutting minor
   * releases). Surfaced as a badge. KBE-684. */
  isReleaseBranch?: boolean;
  /** A branch cut from a protected ref rather than a plain duplicate — its base is
   * that ref's frozen head commit, not a hidden fork copy (KBE-793).
   *
   * Labelled "Ref branch" rather than "Branch" on purpose: an ordinary duplicate is
   * also a branch of its parent and carries no badge, so "Branch" would read as if
   * the others were not branches at all. Kept to a gray outline because a
   * materialized ref branch behaves like any working copy — the label names what
   * kind of network it is, it does not warn about anything. */
  isRefBranch?: boolean;
  /** Explicit version string, or null/undefined when unset. KBE-586. */
  version?: string | null;
  /** Name of the network this one was duplicated from, or null when it
   * wasn't forked / the source was deleted. KBE-586. */
  forkedFromName?: string | null;
  /** When the network was retired, or null when active. KBE-545. Surfaced
   * as a "Retired" badge; retired networks only appear when "Show retired"
   * is toggled on. */
  retiredAt?: string | null;
  /** View options only: the view is part of a frozen release, so it can be
   * opened but not renamed, deleted or rearranged. Views added to a released
   * network afterwards stay editable. KBE-715. */
  isViewLocked?: boolean;
};
export enum Title {
  NETWORK = 'network',
  VIEW = 'view',
}
export type SimpleMenuItem = {
  menuType: 'simple';
  props: {
    title: string;
    onClick: (option: Option) => void;
    icon: React.ReactElement;
    disabled?: boolean;
  };
};
export type InputMenuItem = {
  menuType: 'input';
  props: {
    actionTitle: string;
    icon: React.ReactElement;
    modalTitle: string;
    defaultInputValue: (option: Option) => string;
    preventSavingDefaultValue?: boolean;
    /** Allow submitting an empty value (e.g. to clear a field). */
    allowEmpty?: boolean;
    onSubmit: (input: string, option: Option, isReleaseBranch?: boolean, copyKind?: CopyKind) => unknown;
    disabled?: boolean;
    /** KBE-882: offers a "Create as delta branch" switch in the modal. */
    deltaConfig?: {
      label: string;
    };
    /** KBE-981: offers an explicit "Create as ref branch" switch in the modal. */
    refBranchConfig?: {
      label: string;
      warning: string;
    };
    /** Offers a "Create working copy" / "Create release branch" choice in the
     * modal. Resolved per-option at open time: returns the auto-computed
     * release-branch name, or null when a release branch can't be created from
     * that option (only from the master), in which case no choice is shown.
     * See KBE-685. */
    intentConfig?: {
      releaseBranchName: (option: Option) => string | null;
    };
  };
};
export type SelectMenuItem = {
  menuType: 'select';
  props: {
    title: (option: Option) => string;
    icon: React.ReactElement;
    modalTitle: string;
    options: string[];
    currentValue: (option: Option) => string | null;
    onSubmit: (value: string, option: Option) => unknown;
    disabled?: boolean;
  };
};
export type MenuItems = (id?: string) => Array<InputMenuItem | SimpleMenuItem | SelectMenuItem>;

// Resolves the per-option release-branch name for the InputModal. Returns
// undefined (no choice shown) when the menu item has no intentConfig or the
// option can't spawn a release branch (only the master can). See KBE-685.
function resolveIntentConfig(intentConfig: InputMenuItem['props']['intentConfig'], option: Option): {
  releaseBranchName: string;
} | undefined {
  const releaseBranchName = intentConfig?.releaseBranchName(option);
  return releaseBranchName ? {
    releaseBranchName
  } : undefined;
}

/** Inline-only row selection for bulk actions (KBE-964). */
export type OptionSelection = {
  selectedIds: ReadonlySet<string>;
  /** Rows no bulk action applies to (the master) render a disabled box. */
  isSelectable: (option: Option) => boolean;
  onToggle: (id: string) => void;
  onSetSelected: (ids: string[], selected: boolean) => void;
};
type VirtualizedSelectPopoverProps = {
  menuItems?: MenuItems;
  icon: React.ReactElement;
  isLoading?: boolean;
  isDefaultOpen: boolean;
  options: Array<Option>;
  currentOption: string | undefined;
  currentOptionName?: string;
  onChangeCurrentOption: (id: string) => void;
  onCreate?: (name: string) => void;
  title: Title;
  refOfElementsToIgnoreOutsideClick?: React.RefObject<HTMLDivElement | null>[];
  headerItem?: ReactNode;
  shouldPreventClose?: () => boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Render as an inline page widget instead of a popover. Skips the
   * PopoverRoot/Trigger/Content wrapping and renders the header+list
   * directly. Used by the Network browser page so it can reuse the
   * full feature set (rename, duplicate, delete, compare, export,
   * release, report, algorithm, base population) without duplication.
   */
  inline?: boolean;
  /**
   * Inline-only: rendered in place of the table body when there are
   * zero options to show (e.g. "No networks edited by Sam"). Caller
   * builds the message because only they know whether the list is
   * empty because of a filter vs because the DB has no networks.
   */
  inlineEmptyState?: ReactNode;
  /**
   * Anchor for the popover relative to its trigger. Defaults to
   * 'bottom-start' (anchored on the trigger's left edge). Pass
   * 'bottom-end' for triggers near the right edge of the viewport so
   * the popover extends LEFT into visible space.
   */
  popoverPlacement?: 'bottom-start' | 'bottom-end' | 'bottom';
  /**
   * Infinite scroll: when `hasMore` is true the component renders a
   * sentinel below the list that fires `onLoadMore` when the user
   * scrolls within ~`ITEM_HEIGHT * OVERSCAN` of the bottom. While the
   * caller is fetching the next page it should set `isFetchingMore`
   * so the sentinel shows a loader instead of re-firing.
   */
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  /**
   * Inline-only: active sort column + direction and the click handler.
   * When `onSort` is set, the `created` and `lastEdited` column headers
   * become clickable sort toggles with a direction indicator.
   */
  sortColumn?: SortableColumnKey;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: SortableColumnKey) => void;
  selection?: OptionSelection;
};
const ITEM_HEIGHT = 60;
const OVERSCAN = 5;
const VIRTUALIZATION_THRESHOLD = 20;

// Inline Home-browser column layout. The kebab column is fixed; the seven
// content columns are user-resizable and their widths are persisted. Any
// leftover horizontal space pools into a flexible filler placed just before
// the kebab so the rightmost action button stays pinned to the edge.
const KEBAB_COLUMN_WIDTH = 44;
const SELECTION_COLUMN_WIDTH = 24;
const MIN_COLUMN_WIDTH = 60;
const COLUMN_WIDTHS_STORAGE_KEY = 'networkBrowserColumnWidths';
const DEFAULT_COLUMN_WIDTHS = {
  name: 340,
  status: 96,
  owner: 110,
  created: 120,
  lastEdited: 130,
  editedBy: 150
};
type ColumnWidths = typeof DEFAULT_COLUMN_WIDTHS;
type ColumnKey = keyof ColumnWidths;
/** The inline browser columns that support click-to-sort. */
type SortableColumnKey = Extract<ColumnKey, 'created' | 'lastEdited'>;
const isSortableColumn = (key: ColumnKey): key is SortableColumnKey => key === 'created' || key === 'lastEdited';
const RESIZABLE_COLUMN_ORDER: ColumnKey[] = ['name', 'status', 'owner', 'created', 'lastEdited', 'editedBy'];
const loadColumnWidths = (): ColumnWidths => {
  if (typeof window === 'undefined') return DEFAULT_COLUMN_WIDTHS;
  try {
    const raw = window.localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMN_WIDTHS;
    const parsed = JSON.parse(raw) as Partial<Record<ColumnKey, unknown>>;
    const merged = {
      ...DEFAULT_COLUMN_WIDTHS
    };
    for (const key of RESIZABLE_COLUMN_ORDER) {
      const value = parsed[key];
      if (typeof value === 'number' && value >= MIN_COLUMN_WIDTH) {
        merged[key] = value;
      }
    }
    return merged;
  } catch {
    return DEFAULT_COLUMN_WIDTHS;
  }
};
const ColumnResizer = t0 => {
  const $ = _c(9);
  const {
    ariaLabel,
    onResize
  } = t0;
  const lastXRef = useRef(0);
  const cleanupRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => cleanupRef.current?.();
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== onResize) {
    t3 = event => {
      event.preventDefault();
      event.stopPropagation();
      lastXRef.current = event.clientX;
      const handleMove = moveEvent => {
        const delta = moveEvent.clientX - lastXRef.current;
        lastXRef.current = moveEvent.clientX;
        if (delta !== 0) {
          onResize(delta);
        }
      };
      const stopResizing = () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", stopResizing);
        window.removeEventListener("pointercancel", stopResizing);
        cleanupRef.current = null;
      };
      cleanupRef.current = stopResizing;
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", stopResizing);
      window.addEventListener("pointercancel", stopResizing);
    };
    $[2] = onResize;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handlePointerDown = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "& > span": {
        backgroundColor: "blue.500",
        width: "3px"
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Box as="span" backgroundColor="gray.300" borderRadius="full" height="14px" transition="background-color 0.1s ease, width 0.1s ease" width="2px" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== ariaLabel || $[7] !== handlePointerDown) {
    t6 = <Box _hover={t4} alignItems="center" aria-label={ariaLabel} bottom={0} cursor="col-resize" display="flex" justifyContent="center" position="absolute" right="-7px" role="separator" top={0} width="14px" zIndex={1} onClick={_temp} onPointerDown={handlePointerDown}>{t5}</Box>;
    $[6] = ariaLabel;
    $[7] = handlePointerDown;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};
const VirtualizedSelectPopover = ({
  refOfElementsToIgnoreOutsideClick = [],
  menuItems,
  icon,
  title,
  isLoading,
  isDefaultOpen,
  options,
  currentOption,
  currentOptionName,
  onChangeCurrentOption,
  headerItem,
  shouldPreventClose,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  inline = false,
  inlineEmptyState,
  popoverPlacement = 'bottom-start',
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
  sortColumn,
  sortDirection,
  onSort,
  selection
}: VirtualizedSelectPopoverProps) => {
  // React Compiler memoizes this component, but the TanStack Virtual
  // instance (reached via useSafeVirtualizer) is a stable reference whose
  // scroll state mutates internally -- the compiler can't see that, so it
  // serves a stale render and the rows freeze/vanish on scroll. The
  // useVirtualizer incompatibility is hidden behind the wrapper, so the
  // compiler's own auto-bailout never triggers; this directive restores it.
  'use no memo';

  const router = useRouter();
  const isCurrent = useCallback((id: string) => currentOption === id, [currentOption]);
  const {
    open: internalOpen,
    onClose: internalOnClose,
    onOpen: internalOnOpen
  } = useDisclosure();

  // Inline mode is always-open; controlled/internal otherwise.
  const open = inline ? true : controlledIsOpen !== undefined ? controlledIsOpen : internalOpen;
  const onClose = useMemo(() => controlledOnOpenChange ? () => controlledOnOpenChange(false) : internalOnClose, [controlledOnOpenChange, internalOnClose]);
  const onOpen = useMemo(() => controlledOnOpenChange ? () => controlledOnOpenChange(true) : internalOnOpen, [controlledOnOpenChange, internalOnOpen]);
  const [inputModalProps, setInputModalProps] = useState<Omit<InputModalProps, 'isOpen' | 'onClose' | 'ref'> | undefined>(undefined);
  const [inferenceAlgorithmModalProps, setInferenceAlgorithmModalProps] = useState<Omit<InferenceAlgorithmModalProps, 'isOpen' | 'onClose' | 'ref'> | undefined>(undefined);
  const popoverTriggerRef = useRef<HTMLButtonElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // Track when scroll container is ready for virtualization
  const [isReady, setIsReady] = useState(false);

  // User-resizable column widths for the inline Home browser (persisted).
  // Only the inline instance owns this key — the non-inline header popovers
  // (which never render the column grid) must not write it, or their
  // SSR-default state could clobber widths the user saved on the Home page.
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(loadColumnWidths);
  useEffect(() => {
    if (!inline || typeof window === 'undefined') return;
    window.localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths));
  }, [inline, columnWidths]);
  const handleColumnResize = useCallback((key: ColumnKey, deltaX: number): void => {
    setColumnWidths(prev => ({
      ...prev,
      [key]: Math.max(MIN_COLUMN_WIDTH, Math.round(prev[key] + deltaX))
    }));
  }, []);

  // Decide whether to virtualize based on list size. Inline mode (the
  // Home page network browser) is always windowed — it can render
  // hundreds of rows and the parent gives it a bounded scroll viewport.
  const shouldVirtualize = inline || options.length > VIRTUALIZATION_THRESHOLD;

  // Initialize virtualizer (always called unconditionally - Rules of Hooks)
  const virtualizer = useSafeVirtualizer({
    count: isReady && shouldVirtualize ? options.length : 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ITEM_HEIGHT,
    // Rows are React-keyed by option id, so filtering or re-sorting the list
    // reuses a DOM node at a new index instead of recreating it. The default
    // index-keyed measurement cache then keeps the previous occupant's height
    // for that index, and no ResizeObserver callback corrects it -- the reused
    // node's own box never changed. Result: the KBE-601 overlap returns as soon
    // as the list reorders. Keying measurements by option id keeps the cache
    // aligned with the React key (KBE-679).
    getItemKey: index => options[index]?.id ?? index,
    overscan: OVERSCAN
  });
  const findOptionName = (selected: string) => {
    const option_0 = options.find(option => selected === option.id);
    return option_0?.name;
  };

  // Handle popover open state
  useEffect(() => {
    if (isDefaultOpen) {
      onOpen();
    }
  }, [isDefaultOpen, onOpen]);

  // Wait for scroll container to be mounted before enabling virtualization.
  // Inline mode is always open — no popover-mount race — so we can flip
  // isReady once the ref is attached without the double-RAF dance. The
  // teardown (not-open/not-virtualizing) case is handled by this effect's
  // own cleanup below rather than a synchronous setState at the top of the
  // body, so the reset still runs at the same point -- right before this
  // effect re-fires with new deps, or unmounts -- without a direct
  // setState() call in the effect body itself.
  useEffect(() => {
    if (!open || !shouldVirtualize) {
      return;
    }
    if (inline) {
      if (scrollContainerRef.current) setIsReady(true);
      return;
    }
    let rafId2: number | undefined;
    // Double RAF ensures Chakra UI popover is fully rendered
    const rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          setIsReady(true);
        }
      });
    });
    return () => {
      cancelAnimationFrame(rafId1);
      if (rafId2 !== undefined) cancelAnimationFrame(rafId2);
    };
  }, [open, shouldVirtualize, inline]);
  useEffect(() => () => {
    setIsReady(false);
    hasScrolledRef.current = false;
  }, [open, shouldVirtualize]);

  // Fire onLoadMore when the user scrolls within the overscan window
  // of the last loaded option. Gated on `isFetchingMore` so we don't
  // refire on every scroll tick during an in-flight fetch.
  useEffect(() => {
    if (!hasMore || isFetchingMore || !onLoadMore) return;
    if (!isReady || !shouldVirtualize) return;
    const virtualItems = virtualizer.getVirtualItems();
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;
    if (lastItem.index >= options.length - OVERSCAN) {
      onLoadMore();
    }
  });

  // Auto-scroll to current option when popover opens
  useEffect(() => {
    if (isReady && currentOption && !hasScrolledRef.current) {
      const currentIndex = options.findIndex(opt => opt.id === currentOption);
      if (currentIndex !== -1) {
        const rafId = requestAnimationFrame(() => {
          virtualizer.scrollToIndex(currentIndex, {
            align: 'center',
            behavior: 'auto'
          });
          hasScrolledRef.current = true;
        });
        return () => {
          cancelAnimationFrame(rafId);
        };
      }
    }
    if (!open) {
      hasScrolledRef.current = false;
    }
    return undefined;
  }, [isReady, currentOption, open, virtualizer, options]);
  useHandleOutsideClick({
    insideElements: [...refOfElementsToIgnoreOutsideClick, popoverTriggerRef, popoverContentRef, modalRef, menuContentRef],
    onOutsideClick: () => {
      if (shouldPreventClose && shouldPreventClose()) {
        return;
      }
      onClose();
    }
  });
  const createButtonText = () => {
    if (currentOption) {
      if (currentOptionName) {
        return currentOptionName;
      }
      const optionName = findOptionName(currentOption);
      if (optionName) return optionName;
      // An id that isn't in `options` is only pending while the list is
      // actually loading. Otherwise it is an id this list will never contain
      // (e.g. a view belonging to another network), and "Loading..." would sit
      // there forever claiming otherwise (KBE-718).
      return isLoading ? 'Loading...' : `Select ${title}`;
    }
    return `Select ${title}`;
  };
  const renderMenu = useCallback((menuItems_0: MenuItems, option_1: Option) => <MenuRoot>
        <MenuTrigger asChild aria-label={`${inline ? 'inline-' : ''}options-${option_1.name}`} ml={2}>
          <IconButton size="xs" title="Options" variant="outline">
            <IoEllipsisVertical />
          </IconButton>
        </MenuTrigger>
        {open && <MenuContent ref={menuContentRef} zIndex={Z_INDEX.contextMenu}>
            {menuItems_0(option_1.id)?.map(({
        menuType,
        props
      }) => {
        if (menuType === 'input') {
          return <MenuItem key={`${props.actionTitle.toLowerCase()}-${option_1.id}`} disabled={props.disabled} value={props.actionTitle} onClick={() => {
            setInputModalProps({
              ...props,
              buttonText: props.actionTitle,
              onSubmit: (input: string, isReleaseBranch?: boolean, copyKind?: CopyKind) => props.onSubmit(input, option_1, isReleaseBranch, copyKind),
              defaultInputValue: props.defaultInputValue(option_1),
              intentConfig: resolveIntentConfig(props.intentConfig, option_1),
              deltaConfig: props.deltaConfig,
              refBranchConfig: props.refBranchConfig
            });
          }}>
                    {props.actionTitle}
                  </MenuItem>;
        }
        if (menuType === 'simple') {
          return <MenuItem {...props} key={`${props.title.toLowerCase()}-${option_1.id}`} value={props.title} onClick={() => props.onClick(option_1)}>
                    {props.title}
                  </MenuItem>;
        }
        if (menuType === 'select') {
          const menuTitle = props.title(option_1);
          return <MenuItem key={`${menuTitle.toLowerCase()}-${option_1.id}`} value={menuTitle} onClick={() => {
            setInferenceAlgorithmModalProps({
              networkName: option_1.name,
              availableAlgorithms: props.options as InferenceAlgorithm[],
              currentAlgorithm: props.currentValue(option_1) as InferenceAlgorithm,
              onAlgorithmChange: value => props.onSubmit(value, option_1)
            });
          }}>
                    {menuTitle}
                  </MenuItem>;
        }
        return undefined;
      })}
          </MenuContent>}
      </MenuRoot>, [open, inline]);
  const renderOption = useCallback((option_2: Option) => <Flex alignItems="center" aria-label={`${inline ? 'inline-' : ''}${title}-option-button-${option_2.name}`} backgroundColor={isCurrent(option_2.id) ? 'gray.100' : undefined} p={2}>
        <Link href={option_2.url} style={{
      width: '100%',
      alignSelf: 'stretch'
    }}>
          <Flex alignItems="center" height="100%" justifyContent="space-between" role="button" width="100%" onClick={() => {
        onChangeCurrentOption(option_2.id);
        onClose();
      }}>
            <Flex flexDir="column" minW={0}>
              <Flex alignItems="center" gap={2}>
                <Text fontWeight={isCurrent(option_2.id) ? 'bold' : undefined}>
                  {option_2.name}
                </Text>
                {option_2.status === NetworkStatus.RELEASED && <Badge colorPalette="green" size="sm" variant="subtle">
                    Released
                  </Badge>}
                {/* Show the inference algorithm only in inline mode —
                    the popover row is too narrow to fit comfortably. */}
                {inline && option_2.inferenceAlgorithm && <Badge colorPalette="gray" size="sm" variant="subtle">
                    {option_2.inferenceAlgorithm}
                  </Badge>}
                {option_2.marvinWritable && <Badge aria-label={`${option_2.name}-marvin-copy-badge`} colorPalette="purple" size="sm" variant="subtle">
                    Marvin copy
                  </Badge>}
                {option_2.isMain && <Badge aria-label={`${option_2.name}-main-badge`} colorPalette="teal" size="sm" variant="solid">
                    Main
                  </Badge>}
                {option_2.isRegularCopy && <Badge aria-label={`${option_2.name}-regular-copy-badge`} colorPalette="orange" size="sm" variant="subtle">
                    Regular copy
                  </Badge>}
                {option_2.isReleaseBranch && <Badge aria-label={`${option_2.name}-release-branch-badge`} colorPalette="cyan" size="sm" variant="subtle">
                    Release branch
                  </Badge>}
                {option_2.isRefBranch && <Badge aria-label={`${option_2.name}-ref-branch-badge`} colorPalette="gray" size="sm" variant="outline">
                    Ref branch
                  </Badge>}
                {option_2.version && <Badge aria-label={`${option_2.name}-version-badge`} colorPalette="blue" size="sm" variant="outline">
                    v{option_2.version}
                  </Badge>}
                {option_2.retiredAt && <Badge aria-label={`${option_2.name}-retired-badge`} colorPalette="red" size="sm" variant="subtle">
                    Retired
                  </Badge>}
                {option_2.isViewLocked && <Badge aria-label={`${option_2.name}-locked-badge`} colorPalette="gray" size="sm" variant="subtle">
                    Locked
                  </Badge>}
              </Flex>
              {'ownerName' in option_2 && option_2.ownerName && <Text color="gray" fontSize="sm" fontStyle="italic">
                  {option_2.ownerName}
                </Text>}
              {option_2.forkedFromName && <Text color="gray.500" fontSize="sm">
                  Forked from {option_2.forkedFromName}
                </Text>}
              {inline && option_2.lastEditor && <Text color="gray.500" fontSize="sm">
                  Last edited by {formatEditor(option_2.lastEditor)}
                </Text>}
            </Flex>
            {'editedOn' in option_2 && <Text color="gray" fontSize="sm" fontStyle="italic">
                {option_2.editedOn}
              </Text>}
          </Flex>
        </Link>
        {menuItems && renderMenu(menuItems, option_2)}
      </Flex>, [inline, title, isCurrent, onChangeCurrentOption, onClose, menuItems, renderMenu]);
  const renderLoadMoreFooter = (): React.ReactElement | null => {
    if (!isFetchingMore) return null;
    return <Flex alignItems="center" aria-label={`${title}-load-more-spinner`} gap={2} justifyContent="center" minHeight="40px" p={2}>
        <Spinner size="sm" />
        <Text color="gray.500" fontSize="sm">
          Loading more…
        </Text>
      </Flex>;
  };
  const renderVirtualizedContent = () => {
    const virtualItems_0 = virtualizer.getVirtualItems();
    return <>
        <Box position="relative" style={{
        height: `${virtualizer.getTotalSize()}px`,
        minHeight: '100px'
      }}>
          {virtualItems_0.map(virtualRow => {
          const option_3 = options[virtualRow.index];
          return (
            // Popover rows have variable height (name + badges, an
            // optional italic owner line, and an optional "Forked
            // from …" subtitle). ITEM_HEIGHT is only an estimate, so
            // each row is measured via `measureElement`/`data-index`;
            // without this, taller rows overlap the next one.
            <Box key={`${title}-${option_3.id}`} ref={virtualizer.measureElement} data-index={virtualRow.index} left={0} position="absolute" style={{
              transform: `translateY(${virtualRow.start}px)`
            }} top={0} width="100%">
                {renderOption(option_3)}
                <Separator />
              </Box>
          );
        })}
        </Box>
        {renderLoadMoreFooter()}
      </>;
  };
  const renderNonVirtualizedContent = () => options.map(option_4 => <Fragment key={`${title}-${option_4.id}`}>
        {renderOption(option_4)}
        <Separator />
      </Fragment>);

  /**
   * Full-width row layout used only by the inline Home page network
   * browser. Each option renders one row of size/ownership stats; the
   * per-row kebab reuses the same `menuItems` action set as the popover.
   *
   * Rows are entirely clickable — clicking anywhere on a row (except
   * the kebab) navigates as if the name link were clicked. The first
   * cell still hosts a real <Link> so middle-click / cmd-click /
   * keyboard focus still work as expected.
   *
   * The body is windowed via tanstack-virtual: at 900+ networks the
   * naive `options.map(...)` rendered ~9,000 cells on mount.
   */
  const inlineColumnGrid = `${selection ? `${SELECTION_COLUMN_WIDTH}px ` : ''}${RESIZABLE_COLUMN_ORDER.map(key_0 => `${columnWidths[key_0]}px`).join(' ')} minmax(0, 1fr) ${KEBAB_COLUMN_WIDTH}px`;
  const selectableOptions = selection ? options.filter(option_5 => selection.isSelectable(option_5)) : [];
  const selectedLoadedCount = selection ? selectableOptions.filter(option_6 => selection.selectedIds.has(option_6.id)).length : 0;
  const allLoadedSelected = selectableOptions.length > 0 && selectedLoadedCount === selectableOptions.length;
  const renderSelectAllCell = (): ReactNode => selection && <Checkbox aria-label="select-all-loaded-networks" checked={allLoadedSelected ? true : selectedLoadedCount > 0 ? 'indeterminate' : false} disabled={selectableOptions.length === 0} size="sm" onCheckedChange={() => selection.onSetSelected(selectableOptions.map(option_7 => option_7.id), !allLoadedSelected)} />;
  const renderRowSelectionCell = (option_8: Option): ReactNode => selection &&
  // The row navigates on click, so a near-miss on a small box opened the
  // network. The checkbox's label instead fills everything left of the name
  // column: full row height, into the row's left padding and the column gap.
  <Box alignSelf="stretch" display="flex" ml={-4} mr={-3} onClick={event => event.stopPropagation()}>
        <Checkbox alignItems="center" aria-label={`select-${title}-${option_8.name}`} checked={selection.selectedIds.has(option_8.id)} cursor={selection.isSelectable(option_8) ? 'pointer' : 'default'} disabled={!selection.isSelectable(option_8)} flex="1" pl={4} pr={3} size="sm" onCheckedChange={() => selection.onToggle(option_8.id)} />
      </Box>;
  const renderHeaderCell = (key_1: ColumnKey, label: string): ReactNode => {
    const sortable = !!onSort && isSortableColumn(key_1);
    const isActiveSort = sortable && sortColumn === key_1;
    const sortIcon = !sortable ? null : !isActiveSort ? <Box as="span" color="gray.400">
        <LuChevronsUpDown size={12} />
      </Box> : sortDirection === 'asc' ? <LuArrowUp size={12} /> : <LuArrowDown size={12} />;
    return <Box overflow="hidden" position="relative" pr={2} textOverflow="ellipsis">
        {sortable ? <chakra.button alignItems="center" aria-label={`sort-by-${key_1}`} aria-sort={isActiveSort ? sortDirection === 'asc' ? 'ascending' : 'descending' : 'none'} color={isActiveSort ? 'gray.900' : undefined} cursor="pointer" display="flex" gap={1} type="button" onClick={() => onSort(key_1)}>
            {label}
            {sortIcon}
          </chakra.button> : label}
        <ColumnResizer ariaLabel={`resize-${key_1}-column`} onResize={delta => handleColumnResize(key_1, delta)} />
      </Box>;
  };
  const renderInlineHeader = () => <Box alignItems="center" bg="gray.50" borderBottomColor="gray.200" borderBottomWidth="1px" borderLeftColor="transparent" borderLeftWidth="3px" color="gray.600" display="grid" fontSize="xs" fontWeight="semibold" gap={3} gridTemplateColumns={inlineColumnGrid} letterSpacing="wide" px={4} py={2.5}>
      {renderSelectAllCell()}
      {renderHeaderCell('name', 'Name')}
      {renderHeaderCell('status', 'Status')}
      {renderHeaderCell('owner', 'Owner')}
      {renderHeaderCell('created', 'Created')}
      {renderHeaderCell('lastEdited', 'Last edited')}
      {renderHeaderCell('editedBy', 'Edited by')}
      <Box />
      <Box />
    </Box>;
  const renderInlineRow = (option_9: Option): React.ReactElement => {
    const isReleased = option_9.status === NetworkStatus.RELEASED;
    const isActive = isCurrent(option_9.id);
    const handleRowNavigate = (event_0: React.MouseEvent<HTMLElement>): void => {
      // Let cmd/ctrl/shift/middle-click fall through to the
      // native <Link> in the first cell so the user can open
      // the network in a new tab the same way browsers expect.
      if (event_0.button !== 0 || event_0.metaKey || event_0.ctrlKey || event_0.shiftKey || event_0.altKey) {
        return;
      }
      onChangeCurrentOption(option_9.id);
      onClose();
      router.push(option_9.url);
    };
    return <Box _hover={{
      bg: isActive ? 'blue.100' : 'gray.50'
    }} alignItems="center" aria-label={`inline-${title}-option-button-${option_9.name}`} backgroundColor={isActive ? 'blue.50' : undefined} borderBottomColor="gray.100" borderBottomWidth="1px" borderLeftColor={isActive ? 'blue.500' : 'transparent'} borderLeftWidth="3px" cursor="pointer" display="grid" gap={3} gridTemplateColumns={inlineColumnGrid} height="100%" opacity={isReleased ? 0.72 : 1} px={4} transition="background-color 0.12s ease" onClick={handleRowNavigate}>
        {renderRowSelectionCell(option_9)}
        <Flex alignItems="center" gap={2} minW={0}>
          {isReleased && <Icon aria-label={`${option_9.name}-released-lock`} as={LuLock} boxSize="14px" color="gray.400" flexShrink={0} />}
          <Box flex="1" minW={0}>
            <Link href={option_9.url} prefetch={false} style={{
            display: 'block'
          }} onClick={e => {
            // Row's onClick already handles SPA navigation;
            // stop the Link from triggering it a second time
            // while still letting middle-click / cmd-click
            // open in a new tab (those go through the native
            // anchor behavior, not this click handler).
            e.stopPropagation();
            onChangeCurrentOption(option_9.id);
            onClose();
          }}>
              <Text color={isActive ? 'blue.700' : 'gray.900'} fontWeight={isActive ? 'bold' : 'medium'} overflow="hidden" textOverflow="ellipsis" title={option_9.name} whiteSpace="nowrap">
                {option_9.name}
              </Text>
            </Link>
            {option_9.forkedFromName && <Text color="gray.500" fontSize="xs" overflow="hidden" textOverflow="ellipsis" title={`Forked from ${option_9.forkedFromName}`} whiteSpace="nowrap">
                Forked from {option_9.forkedFromName}
              </Text>}
          </Box>
          {option_9.isMain && <Badge aria-label={`${option_9.name}-main-badge`} colorPalette="teal" flexShrink={0} size="sm" variant="solid">
              Main
            </Badge>}
          {option_9.isRegularCopy && <Badge aria-label={`${option_9.name}-regular-copy-badge`} colorPalette="orange" flexShrink={0} size="sm" variant="subtle">
              Regular copy
            </Badge>}
          {option_9.isReleaseBranch && <Badge aria-label={`${option_9.name}-release-branch-badge`} colorPalette="cyan" flexShrink={0} size="sm" variant="subtle">
              Release branch
            </Badge>}
          {option_9.isRefBranch && <Badge aria-label={`${option_9.name}-ref-branch-badge`} colorPalette="gray" flexShrink={0} size="sm" variant="outline">
              Ref branch
            </Badge>}
          {option_9.version && <Badge aria-label={`${option_9.name}-version-badge`} colorPalette="blue" flexShrink={0} size="sm" variant="outline">
              v{option_9.version}
            </Badge>}
          {option_9.marvinWritable && <Badge aria-label={`${option_9.name}-marvin-copy-badge`} colorPalette="purple" flexShrink={0} size="sm" variant="subtle">
              Marvin
            </Badge>}
          {option_9.retiredAt && <Badge aria-label={`${option_9.name}-retired-badge`} colorPalette="red" flexShrink={0} size="sm" variant="subtle">
              Retired
            </Badge>}
        </Flex>
        <Box>
          {isReleased ? <Badge colorPalette="green" size="sm" variant="subtle">
              Released
            </Badge> : <Text color="gray.500" fontSize="sm">
              Working
            </Text>}
        </Box>
        <Text color="gray.700" fontSize="sm" overflow="hidden" textOverflow="ellipsis" title={option_9.ownerName ?? undefined} whiteSpace="nowrap">
          {option_9.ownerName ? formatEditor(option_9.ownerName) : '—'}
        </Text>
        <Text color="gray.700" fontSize="sm" title={option_9.createdOnExact ?? undefined}>
          {option_9.createdOn ?? '—'}
        </Text>
        <Text color="gray.700" fontSize="sm" title={option_9.editedOnExact ?? undefined}>
          {option_9.editedOn ?? '—'}
        </Text>
        <Text color="gray.700" fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {option_9.lastEditor ? formatEditor(option_9.lastEditor) : '—'}
        </Text>
        <Box />
        <Box textAlign="end" onClick={e_0 => {
        // Don't fire the row navigation when the user
        // clicks the kebab — they want the menu.
        e_0.stopPropagation();
      }}>
          {menuItems && renderMenu(menuItems, option_9)}
        </Box>
      </Box>;
  };
  const renderInlineBody = (): React.ReactElement => {
    if (options.length === 0) {
      return <Box p={8} textAlign="center">
          {inlineEmptyState ?? <Text color="gray.500">No options to show.</Text>}
        </Box>;
    }
    // Until the scroll container has mounted (first render, before the
    // ref attaches) the virtualizer reports zero virtual items. Render
    // a small non-virtual slice so the user sees content immediately;
    // the next render swaps in the windowed list.
    if (!isReady) {
      const previewCount = Math.min(options.length, VIRTUALIZATION_THRESHOLD);
      return <Box>
          {options.slice(0, previewCount).map(option_10 => <Box key={`${title}-${option_10.id}`} height={`${ITEM_HEIGHT}px`}>
              {renderInlineRow(option_10)}
            </Box>)}
        </Box>;
    }
    const virtualItems_1 = virtualizer.getVirtualItems();
    return <>
        <Box position="relative" style={{
        height: `${virtualizer.getTotalSize()}px`
      }}>
          {virtualItems_1.map(virtualRow_0 => {
          const option_11 = options[virtualRow_0.index];
          return <Box key={`${title}-${option_11.id}`} left={0} position="absolute" style={{
            transform: `translateY(${virtualRow_0.start}px)`,
            height: `${virtualRow_0.size}px`
          }} top={0} width="100%">
                {renderInlineRow(option_11)}
              </Box>;
        })}
        </Box>
        {renderLoadMoreFooter()}
      </>;
  };
  const bodyContent = isLoading ? inline ? <Flex direction="column" gap={2} p={3}>
        {Array.from({
      length: 5
    }).map((_, i) => <Skeleton key={i} height="48px" />)}
      </Flex> : <Flex alignItems="center" justifyContent="center" minHeight="15rem">
        <Spinner animationDuration="0.65s" aria-label="popover-spinner" borderWidth="4px" size="xl" />
      </Flex> : <>
      <Separator />
      {shouldVirtualize ? isReady ? renderVirtualizedContent() : <Flex alignItems="center" justifyContent="center" minHeight="15rem">
            <Spinner animationDuration="0.65s" aria-label="virtualization-spinner" borderWidth="4px" size="xl" />
          </Flex> : renderNonVirtualizedContent()}
    </>;
  return <>
      {inline ? <Box aria-label={`${title}-browser`} borderColor="gray.200" borderRadius="md" borderWidth="1px" display="flex" flex="1" flexDirection="column" minHeight={0} width="100%">
          <Flex alignItems="center" flexShrink={0} justifyContent="space-between" minHeight={10} p={2}>
            <Box width="100%">{headerItem}</Box>
          </Flex>
          {!isLoading && options.length > 0 && renderInlineHeader()}
          <Box ref={scrollContainerRef} flex="1" minHeight={0} overflowY="auto" p={0}>
            {isLoading ? bodyContent : renderInlineBody()}
          </Box>
        </Box> : <Box display="contents">
          <PopoverRoot autoFocus={false} closeOnInteractOutside={false} open={open} positioning={{
        placement: popoverPlacement
      }} onOpenChange={e_1 => {
        if (!e_1.open) {
          onClose();
        }
      }}>
            <PopoverTrigger ref={popoverTriggerRef} asChild>
              <Button aria-label={`select-${title}-button`} variant="outline" onClick={() => onOpen()}>
                <Text maxWidth="300px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {createButtonText()}
                </Text>{' '}
                {icon}
              </Button>
            </PopoverTrigger>
            <PopoverContent ref={popoverContentRef} aria-label={`${title}-popover`} width="35rem" zIndex={Z_INDEX.selectPopover}>
              <PopoverArrow />
              <PopoverHeader display="flex" justifyContent="space-between" minHeight={10} p={2}>
                <Box marginRight={8} width="100%">
                  {headerItem}
                </Box>
                <PopoverCloseTrigger aria-label={`close-${title}-popover-button`} />
              </PopoverHeader>
              <PopoverBody ref={scrollContainerRef} maxHeight="30rem" overflowY="auto" p={0}>
                {bodyContent}
              </PopoverBody>
            </PopoverContent>
          </PopoverRoot>
        </Box>}
      {inputModalProps && <InputModal ref={modalRef} isOpen={!!inputModalProps} onClose={() => setInputModalProps(undefined)} {...inputModalProps} />}
      {inferenceAlgorithmModalProps && <InferenceAlgorithmModal ref={modalRef} isOpen={!!inferenceAlgorithmModalProps} onClose={() => setInferenceAlgorithmModalProps(undefined)} {...inferenceAlgorithmModalProps} />}
    </>;
};
export default VirtualizedSelectPopover;
function _temp(event_0) {
  return event_0.stopPropagation();
}